import { faker } from '@faker-js/faker'
import type { Payment, PaymentStatus } from '@shared/types/payment.types'
import {
  ID_PREFIX,
  PAYMENT_STATUSES,
  PAYMENT_CHANNELS,
  CHANNEL_ID_PREFIXES,
  CARD_BRANDS,
  PAYMENT_DESCRIPTIONS,
  CURRENCIES,
  MERCHANT_NAMES,
  PROGRAM_NAMES,
  STORE_NAMES,
} from '../data/constants'

// ── Helpers ─────────────────────────────────────────────────────────────────

function createAmount(min = 1000, max = 5000000) {
  const raw = faker.number.int({ min, max })
  const currency = faker.helpers.arrayElement([...CURRENCIES])
  return { amount: raw, decimal: 2, currency }
}

function generateStatusFields(status: PaymentStatus) {
  switch (status) {
    case 'declined':
    case 'blocked':
      return {
        declineReason: faker.helpers.arrayElement([
          'Insufficient funds', 'Card expired', 'Suspected fraud',
          'Do not honor', 'Invalid card number', 'Exceeds limit',
        ]),
        riskScore: String(faker.number.int({ min: 70, max: 99 })),
      }

    case 'succeeded':
    case 'approved':
      return {
        declineReason: '',
        riskScore: String(faker.number.int({ min: 5, max: 30 })),
      }

    case 'failed':
    case 'expired':
      return {
        declineReason: faker.helpers.arrayElement([
          'Network timeout', 'Processing error', 'Authorization expired',
        ]),
        riskScore: String(faker.number.int({ min: 30, max: 60 })),
      }

    default:
      return {
        declineReason: '',
        riskScore: String(faker.number.int({ min: 10, max: 50 })),
      }
  }
}

// ── Public API ──────────────────────────────────────────────────────────────

interface PaymentListContext {
  customers?: Array<{ id: string; name: string; email: string }>
}

export function createPayment(overrides?: Partial<Payment>): Payment {
  const status = overrides?.status ?? faker.helpers.arrayElement([...PAYMENT_STATUSES])
  const amt = createAmount()
  const brand = faker.helpers.arrayElement([...CARD_BRANDS])
  const last4 = faker.finance.creditCardNumber('####')
  const statusFields = generateStatusFields(status)
  const createdAt = faker.date.recent({ days: 90 }).toISOString()
  const channel = overrides?.channel ?? faker.helpers.arrayElement([...PAYMENT_CHANNELS])
  const channelId = `${CHANNEL_ID_PREFIXES[channel] ?? ''}${faker.string.alphanumeric({ length: 6, casing: 'upper' })}`

  const payment: Payment = {
    id: `${ID_PREFIX.payment}${faker.string.nanoid(8)}`,
    amount: amt,
    amounts: {
      original: amt,
      approved: status === 'declined' || status === 'blocked'
        ? { amount: 0, decimal: 2, currency: amt.currency }
        : amt,
      cashback: { amount: 0, decimal: 2, currency: amt.currency },
      billing: amt,
    },
    currency: amt.currency,
    status,
    description: faker.helpers.arrayElement([...PAYMENT_DESCRIPTIONS]),
    customer: {
      id: `${ID_PREFIX.customer}${faker.string.nanoid(6)}`,
      name: faker.person.fullName(),
      email: faker.internet.email().toLowerCase(),
    },
    paymentMethod: {
      type: 'card',
      displayString: `${brand} ****${last4}`,
      last4,
      brand,
    },
    createdAt,
    timestamps: {
      initiated: createdAt,
      authorized: status === 'pending'
        ? ''
        : faker.date.between({ from: createdAt, to: new Date() }).toISOString(),
    },
    transactionRef: faker.string.alphanumeric({ length: 12, casing: 'upper' }),
    account: {
      accountId: `${ID_PREFIX.mmAccount}${faker.string.nanoid(6)}`,
      name: faker.company.name(),
      email: faker.internet.email().toLowerCase(),
    },
    metadata: {},
    lastUpdated: faker.date.recent({ days: 7 }),
    channel,
    channelId,
    eligibility: faker.helpers.arrayElement(['eligible', 'ineligible', 'conditional']),
    store: faker.helpers.arrayElement([...STORE_NAMES]),
    ...statusFields,
    merchant: faker.helpers.arrayElement([...MERCHANT_NAMES]),
    program: faker.helpers.arrayElement([...PROGRAM_NAMES]),
    organization: `${ID_PREFIX.organization}${faker.string.nanoid(4)}`,
    bank: `${ID_PREFIX.bank}${faker.string.nanoid(4)}`,
    productPlatform: faker.helpers.arrayElement(['online', 'pos', 'mobile']),
    processor: faker.helpers.arrayElement(['stripe', 'adyen', 'worldpay', 'checkout']),
    ...overrides,
  }

  return payment
}

export function createPaymentList(
  count: number,
  overrides?: Partial<Payment>,
  context?: PaymentListContext,
): Payment[] {
  return Array.from({ length: count }, (_, i) => {
    const customerOverride = context?.customers
      ? { customer: faker.helpers.arrayElement(context.customers) }
      : {}

    return createPayment({
      id: `${ID_PREFIX.payment}${String(i + 1).padStart(3, '0')}`,
      ...customerOverride,
      ...overrides,
    })
  })
}
